import { useMutation } from '@tanstack/react-query';
import { useCallback, useMemo, useRef } from 'react';

import { ORDENABLE_PAGE_SIZE, REQUEST_STALE_TIME_IN_MS } from '@/constants';
import { ListCacheManager } from '@/data/hook/query/v2/CacheFacade';
import useListService from '@/data/hook/useListService';
import { questionExamGradeQueries } from '@/data/query-keys/questionExamGradeQueryKey';
import {
  createQuestionExamGrade,
  updateQuestionExamGrade,
} from '@/data/services/questionExamGradeServices';
import Activity from '@/models/Activity';
import { Question } from '@/models/Question';
import QuestionExamGrade from '@/models/QuestionExamGrade';
import toQuestionKey from '@/utils/questions/toQuestionKey';
import { hashMap } from '@/utils/shared/hashMap';
import normalize from '@/utils/statistics/normalize';

type UseQuestionExamGradesProps = {
  examAreaId: number;
};

type SaveGradeArgs = {
  question: Question;
  maxGrade: number;
  originalQuestion?: Question;
};

export type QuestionExamGradeContextValue = {
  grades: QuestionExamGrade[];
  cacheManager: ListCacheManager<QuestionExamGrade>;
  isLoading: boolean;
  getGradeForQuestion: (question: Question) => QuestionExamGrade | undefined;
  getActivityTotal: (activity: Activity) => number;
  saveGrade: (args: SaveGradeArgs) => Promise<QuestionExamGrade>;
  refetch: () => Promise<void>;
  isMutating: boolean;
};

const indexGrades = (grades: QuestionExamGrade[]) =>
  hashMap(grades, grade => toQuestionKey(grade.questionType, grade.questionId));

const findGrade = (index: Map<string, QuestionExamGrade>, question: Question) =>
  question.id
    ? index.get(toQuestionKey(question.type, question.id))
    : undefined;

const useQuestionExamGrades = ({
  examAreaId,
}: UseQuestionExamGradesProps): QuestionExamGradeContextValue => {
  const {
    results: grades,
    isInitialLoading,
    cacheManager,
    refetch: refetchGrades,
  } = useListService<QuestionExamGrade>({
    ...questionExamGradeQueries.list({
      examAreaId,
      pageSize: ORDENABLE_PAGE_SIZE,
    }),
    enabled: !!examAreaId,
    staleTime: REQUEST_STALE_TIME_IN_MS,
    keepPreviousData: true,
  });

  const gradesIndex = useMemo(() => indexGrades(grades), [grades]);

  const getGradeForQuestion = useCallback(
    (question: Question) => findGrade(gradesIndex, question),
    [gradesIndex],
  );

  const getActivityTotal = useCallback(
    (activity: Activity): number => {
      const questions = activity.questions ?? [];
      const sum = questions.reduce((sum, question) => {
        const grade = getGradeForQuestion(question);
        return sum + (grade?.maxGrade ?? 0);
      }, 0);
      return normalize(sum, 2);
    },
    [getGradeForQuestion],
  );

  const { mutateAsync: createGrade, isPending: isCreating } = useMutation({
    mutationFn: ({ question, maxGrade }: SaveGradeArgs) =>
      createQuestionExamGrade(examAreaId, {
        questionType: question.type,
        questionId: question.id,
        maxGrade,
      }),
    onSuccess: grade => cacheManager.add(grade),
  });

  const { mutateAsync: updateGrade, isPending: isUpdating } = useMutation({
    mutationFn: ({ id, maxGrade }: { id: number; maxGrade: number }) =>
      updateQuestionExamGrade(examAreaId, id, { maxGrade }),
    onSuccess: grade => cacheManager.update(grade),
  });

  const pendingRefetchRef = useRef<Promise<void>>();

  const refetch = () => {
    const pending = refetchGrades({ throwOnError: true }).then(() => {
      if (pendingRefetchRef.current === pending)
        pendingRefetchRef.current = undefined;
    });
    pendingRefetchRef.current = pending;
    return pending;
  };

  const saveGrade = async ({
    question,
    maxGrade,
    originalQuestion,
  }: SaveGradeArgs): Promise<QuestionExamGrade> => {
    await pendingRefetchRef.current?.catch(refetch);
    const index = indexGrades(cacheManager.get() ?? []);
    let existing =
      findGrade(index, question) ??
      (originalQuestion && findGrade(index, originalQuestion));
    if (existing && existing.questionId !== question.id) {
      existing = { ...existing, questionId: question.id };
      cacheManager.update(existing);
    }
    if (!existing) return createGrade({ question, maxGrade });
    if (existing.maxGrade === maxGrade) return existing;
    return updateGrade({ id: existing.id, maxGrade });
  };

  return {
    grades,
    cacheManager,
    isLoading: isInitialLoading,
    getGradeForQuestion,
    getActivityTotal,
    saveGrade,
    isMutating: isCreating || isUpdating,
    refetch,
  };
};

export default useQuestionExamGrades;
